import type { NotificationReaderContext } from '@/modules/notifications/reads.api'
import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { useCallback, useSyncExternalStore } from 'react'
import {
  isNotificationReaderRefused,
  recoverNotificationReader,
  refuseNotificationReader,
  subscribeReaderRefusals,
} from '@/lib/query/reader-refusals'
import { isNotificationReadUnavailable } from '@/modules/notifications/reads.api'

import type { OwnedReaderSession } from './useOwnedReaderMutation'

export {
  isNotificationReaderRefused,
  refuseNotificationReader,
} from '@/lib/query/reader-refusals'

export async function executeNotificationReader<
  T,
  Context = NotificationReaderContext,
>(
  client: QueryClient,
  key: QueryKey,
  session: OwnedReaderSession<Context>,
  signal: AbortSignal,
  read: (context: Context) => Promise<T>,
  recoverOnSuccess = true,
): Promise<T> {
  isNotificationReaderRefused(client, key)
  try {
    const result = await session.run(signal, read)
    if (recoverOnSuccess) recoverNotificationReader(client, key)
    return result
  } catch (error) {
    if (isNotificationReadUnavailable(error))
      refuseNotificationReader(client, key)
    throw error
  }
}

/** Refusal can change while structurally identical query data stays unchanged. */
export function useNotificationReaderRefused(
  client: QueryClient,
  key: QueryKey,
) {
  const subscribe = useCallback(
    (listener: () => void) => subscribeReaderRefusals(client, listener),
    [client],
  )
  const snapshot = () => isNotificationReaderRefused(client, key)
  return useSyncExternalStore(subscribe, snapshot, snapshot)
}
